// src/components/desk/ContactPill.jsx — the contact popover pill (property contacts +
// party contact book) + its row renderer and audit logger, extracted VERBATIM from
// IntakeTab.jsx (DESK_MODE_REWRITE_PLAN §3 decomposition, behavior-preserving,
// no design changes).

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { t, tLog } from '../../lib/i18n.js'
import { Icon, ROLE_ICON } from '../Icon.jsx'
import { logEvent, getCurrentAdmin } from '../../lib/agentLog.js'
import { checkPhone, splitDial } from '../../lib/phoneRules.js'
import { PLATFORM_LABEL, platformKey } from '../ListingsTab.jsx'
import { BtnLabel, pill, popoverPos, timeAgo, useCloseOnPageMove, useViewer } from './primitives.jsx'
import { fmtPhone } from '../../lib/phone.js'
import {
  addPartyContact, updatePartyContact, revealContactBook,
  getPropertyContacts, addPropertyContact, removePropertyContact, reportAuthorRole, getPhoneReach,
} from '../../db/core.js'

// WhatsApp reach per number (core phone_reach, owner 2026-11-03: "note on the number that this number has no WA").
// One request per card for the numbers it shows, cached for the page's life — a "no" changes rarely and a stale "no"
// only hides one icon. A failed lookup shows nothing, exactly as before.
const reachCache = new Map()   // last 9 digits -> 'no' | 'yes' | null (looked up, nothing known)
const nine = (v) => String(v || '').replace(/\D/g, '').slice(-9)
function useNoWhatsApp(values) {
  const key = [...new Set(values.map(nine).filter(x => x.length === 9))].sort().join(',')
  const [, bump] = useState(0)
  useEffect(() => {
    const missing = key ? key.split(',').filter(x => !reachCache.has(x)) : []
    if (!missing.length) return
    let live = true
    getPhoneReach(missing).then(d => {
      for (const x of missing) reachCache.set(x, d?.reach?.[x] || null)
      if (live) bump(n => n + 1)
    }).catch(() => {})
    return () => { live = false }
  }, [key])
  return (v) => reachCache.get(nine(v)) === 'no'
}

// Audit write for every contact reveal/action — Admin history (agent_logs, category
// 'contact') shows who looked at which contact and what they did with it.
function logContactAction(method, name, phone, context) {
  const who = name || phone || t('desk_contact_unknown_fallback')
  logEvent('property-contact', who, 'contact',
    `${method} · ${who}${phone ? ` (${phone})` : ''}${context ? ` · ${context}` : ''} · by ${getCurrentAdmin()}`)
}
// Contact-role display labels (declared roles stay English in storage per the CLAUDE.md note
// on evidence jsonb filters — this only affects the human-facing text in ContactRow).
// The badge glyph for a row, by the role that row DECLARES (2026-08-10). Reading a
// four-row popover meant reading four right-aligned Georgian words to find the owner; a
// shape is scannable in one pass. Values come from ROLE_ICON, never a glyph name.
//
// ⚠️ ONLY roles that map onto a declared ROLE get one. `neighbor`, `relative`, `other` and
// `keyholder` have no entry and render as text alone — an approximate glyph on a role would
// be a claim about what that person is to this property, and this is the surface an agent
// reads immediately before phoning them. (`keyholder` is deliberately NOT given a key: the
// key belongs to OWNER in ROLE_ICON, and two key-shaped roles one row apart is the exact
// confusion Icon.jsx's naming note warns about.)
const ROLE_GLYPH = { owner: ROLE_ICON.owner, client: ROLE_ICON.client, broker: ROLE_ICON.agent }
const ROLE_LABEL_KEYS = { client: 'desk_role_client', owner: 'desk_role_owner', neighbor: 'desk_role_neighbor', keyholder: 'desk_role_keyholder', broker: 'desk_role_broker', relative: 'desk_role_relative', tenant: 'desk_role_tenant', other: 'desk_role_other' }

// "Is this ad posted by an agent?" (0376) — ONE line at the head of the popover,
// about the AD, not about any single row in it. Listing-scoped by the 2026-07-24
// owner ruling (the same human is owner on one listing and agent on another), which
// is also why it cannot hang off a contact row: a row is a person.
//
// It lives here rather than as a chip on the card face because this is the control an
// agent touches immediately before and after the call — the moment they know the
// answer. B274 is the cautionary tale: the district vote sits where nobody is working
// on that property and has 7 votes against 1,392 disputed rows.
//
// Three sources, three different kinds of evidence, never flattened into one number:
// the portal's own declaration (grey, it is what the poster claimed), the scraped
// agency roster (grey, a dictionary hit), and colleagues who called (the only one
// that can see an agent posting under a private-person account).
// ONE tooltip for every surface that shows this mark — the card-face pill, the popover's
// own verdict row, and anything added later. Two renderings of the same evidence would
// drift, and this one exists precisely to answer "says who?".
//
// Every line NAMES ITS SOURCE, because they are different kinds of evidence and the whole
// feature is about them disagreeing: a colleague who phoned outranks what the site was told,
// which is itself not a verification. No line here is machine-inferred — there is no AI in
// this path at all, and saying "reported by" about a scraped dictionary would be a lie.
export function authorMarkTooltip(mark) {
  if (!mark) return null
  const lines = []
  const yes = mark.reported_agent || 0
  const no = mark.reported_owner || 0
  const ten = mark.reported_tenant || 0
  if (ten) lines.push(tLog('desk_author_role_tip_tenant', { n: ten }))
  if (yes || no) {
    lines.push(yes && no ? tLog('desk_author_role_tip_split', { yes, no })
      : yes ? tLog('desk_author_role_tip_agent', { n: yes })
      : tLog('desk_author_role_tip_owner', { n: no }))
  }
  // WHO, by name and when — an unattributed count is exactly what makes a wrong mark
  // un-correctable: you cannot ask a number why it said that.
  if (yes || no || ten) {
    for (const r of mark.reporters || []) {
      const said = r.answer === 'yes' ? t('desk_author_role_yes') : r.answer === 'tenant' ? t('desk_author_role_tenant') : t('desk_author_role_no')
      lines.push(`  · ${r.name || t('desk_contact_unknown_fallback')} — ${said}${r.at ? ` · ${timeAgo(r.at)}` : ''}`)
    }
  }
  if (mark.declared) lines.push(t(mark.declared === 'agent' ? 'desk_author_role_declared_agent' : 'desk_author_role_declared_owner'))
  if (mark.roster_hit) lines.push(`${t('desk_author_role_roster')}${mark.roster_agency ? ` · ${mark.roster_agency}` : ''}`)
  if (mark.elsewhere > 0) lines.push(tLog('desk_author_role_elsewhere', { n: mark.elsewhere }))
  return lines.length ? lines.join('\n') : null
}

function AgentMarkLine({ mark, onReport }) {
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState(false)
  // "აგენტია" is a DECLARATION about a person, not a preference — owner ruling 2026-09-12:
  // it says this poster is NOT the owner and IS an agent from another agency representing the
  // property. It is one tap away from the phone number an agent is about to call, it feeds the
  // contact chain's rank-9 demotion (a marked agent never becomes `default_party_id`), and the
  // mark is listing-scoped and visible to colleagues. So it asks first.
  //
  // ⚠️ IT CANNOT BE A MODAL, and that is mechanical rather than stylistic. This popover closes
  // on a CAPTURE-PHASE document click for any target outside `btnRef`/`panelRef` (see the
  // effect at the bottom of this file). A <Modal> portals to document.body, i.e. outside both —
  // so the confirm button's own click would dismiss the popover underneath it. The confirmation
  // lives INSIDE the panel for that reason; do not "improve" it into a dialog without moving
  // that outside-click test first.
  //
  // ⚠️ The OWNER side stays one tap. Only the agent side is a claim about somebody's
  // professional identity that demotes them in the contact chain; "მესაკუთრეა" restores the
  // ordinary reading and costs nothing to undo by pressing the other button.
  const [confirming, setConfirming] = useState(false)
  const yes = mark?.reported_agent || 0
  const no = mark?.reported_owner || 0
  // 4206 (owner 2026-11-03, "Third role chip"): the ad's number may be the CURRENT TENANT —
  // neither owner nor agent. A separate answer, never a flavour of "no": reported_owner counts
  // 'no', so folding the tenant into it would make them the owner.
  const ten = mark?.reported_tenant || 0
  const declared = mark?.declared || null
  const roster = !!mark?.roster_hit
  const elsewhere = mark?.elsewhere || 0
  const answered = yes > 0 || no > 0 || ten > 0
  const disputed = [yes, no, ten].filter(n => n > 0).length > 1
  const send = async answer => {
    setBusy(true)
    try { await onReport(answer); setAsking(false); setConfirming(false) } finally { setBusy(false) }
  }
  // What the PORTAL said, always shown when known — it is the claim an agent is
  // correcting, so hiding it would make the correction look unmotivated.
  const portalLine = declared
    ? (declared === 'agent' ? t('desk_author_role_declared_agent') : t('desk_author_role_declared_owner'))
    : roster ? t('desk_author_role_roster') : null
  // ⚠️ ONLY a human answer collapses this to a statement (owner-caught 2026-08-02). It used
  // to collapse on `portalLine` too, so an ad the portal called "owner" — the exact case
  // this feature exists for — rendered "posted as an owner" and buried the question behind
  // a click. The portal's claim is CONTEXT for the question, never a substitute for it.
  if (answered && !asking) {
    const label = disputed ? `${t('desk_author_role_disputed')} ${[yes, no, ten].filter(n => n > 0).join('/')}`
      : yes > 0 ? `${t('desk_author_role_marked')}${yes > 1 ? ` · ${yes}` : ''}`
      : ten > 0 ? t('desk_author_role_tenant')
      : no > 0 ? t('desk_author_role_no')
      : portalLine
    return (
      <button type="button" onClick={() => setAsking(true)} disabled={busy}
        title={[authorMarkTooltip(mark), t('desk_author_role_hint')].filter(Boolean).join('\n')}
        className={`w-full text-left mb-1 pb-1 border-b border-border/40 text-xs rounded hover:bg-accent/40 disabled:opacity-50 ${
          disputed ? 'text-amber-600 dark:text-amber-400' : yes > 0 ? 'text-sky-600 dark:text-sky-400' : 'text-muted-foreground'}`}>
        {/* The glyph FOLLOWS THE ANSWER (2026-08-10). It was a fixed `user-check` on both
            branches, so the row drew the same picture whether colleagues had answered "agent"
            or "owner" — the one bit this line exists to carry lived in the words alone.
            Disputed keeps the agent glyph beside its n/n: the question is still open, and the
            amber is what says so. */}
        {/* No glyph for the tenant: ROLE_ICON has no tenant shape, and the owner's key beside
            "the tenant" would say the opposite of the words. */}
        {(yes > 0 || !ten) && <Icon name={yes > 0 ? ROLE_ICON.agent : ROLE_ICON.owner} size={12} />} {label}
        {/* The read-time projection: the same number reported on other ads. Shown as
            context, never merged into the count above — this listing's report is the
            fact, the rest is a pattern. */}
        {elsewhere > 0 && <span className="opacity-70"> · +{elsewhere}</span>}
      </button>
    )
  }
  return (
    <div className="mb-1 pb-1 border-b border-border/40 text-xs text-muted-foreground">
      {/* What the site claims, above the question it motivates. Muted and iconed so it
          reads as background, not as an answer — "the ad says owner; is that true?" */}
      {portalLine && (
        <div className="flex items-center gap-1 opacity-80" title={mark?.roster_agency || undefined}>
          {/* Same rule one branch down, keyed on what the PORTAL declared — weaker evidence
              than a human answer, and muted for it. `roster_hit` is a dictionary match against
              an agency roster, i.e. an agent claim. */}
          <Icon name={(declared === 'agent' || roster) ? ROLE_ICON.agent : ROLE_ICON.owner} size={12} /> {portalLine}
        </div>
      )}
      {confirming ? (
        /* The declaration, in the agent's own words before they make it. Amber rather than
           red: this is a claim to weigh, not a destructive act — nothing is deleted and the
           other button reverses it. */
        <div className="mt-1 rounded border border-amber-500/40 bg-amber-500/5 px-1.5 py-1">
          <div className="font-medium text-amber-700 dark:text-amber-400">
            <Icon name="alert-triangle" size={12} /> {t('desk_author_role_agent_confirm_title')}
          </div>
          <div className="mt-0.5 leading-[1.45] text-foreground/80">{t('desk_author_role_agent_confirm_body')}</div>
          <div className="flex items-center gap-1.5 mt-1.5">
            <button type="button" disabled={busy} onClick={() => send('yes')}
              className="rounded-full border border-amber-500/60 bg-amber-500/10 px-1.5 leading-[1.5] hover:bg-amber-500/20 disabled:opacity-50">{t('desk_author_role_agent_confirm_yes')}</button>
            <button type="button" disabled={busy} onClick={() => setConfirming(false)}
              className="rounded-full border border-border px-1.5 leading-[1.5] hover:bg-accent/60 disabled:opacity-50">{t('desk_author_role_agent_confirm_cancel')}</button>
          </div>
        </div>
      ) : (
      <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
        {/* Three chips no longer fit beside the question in a 320px popover: the chips wrap,
            the question stays on one line. */}
        <span className="whitespace-nowrap">{t('desk_author_role_q')}</span>
        <button type="button" disabled={busy} onClick={() => setConfirming(true)}
          className="rounded-full border border-border px-1.5 leading-[1.5] hover:bg-accent/60 disabled:opacity-50">{t('desk_author_role_yes')}</button>
        <button type="button" disabled={busy} onClick={() => send('no')}
          className="rounded-full border border-border px-1.5 leading-[1.5] hover:bg-accent/60 disabled:opacity-50">{t('desk_author_role_no')}</button>
        <button type="button" disabled={busy} onClick={() => send('tenant')}
          className="rounded-full border border-border px-1.5 leading-[1.5] hover:bg-accent/60 disabled:opacity-50">{t('desk_author_role_tenant')}</button>
      </div>
      )}
    </div>
  )
}

// One contact as a clean two-line row (the earlier single-line name+number+icons wrapped
// unpredictably and read as clutter):
//   ★ Name                      role
//   599 123 456   📞 💬 ✉      ✎ ✕
// Number itself is click-to-copy. ✎/✕ only on rows with link_id (agent-declared edges —
// the ones an agent added and may edit/remove; resolver-derived rows have no edge to touch).
// Exported for scripts/render-contact-role-ask.mjs. The pill opens on a CLICK — its popover
// is internal state — so a static render of ContactPill yields only the trigger button and
// every assertion about the rows inside it passes over nothing. The row is the unit.
export function ContactRow({ c, isDefault, context, onEdit, onRemove, onMakePrimary, onUnreachable, onAskRole, roleMark = null }) {
  const [copied, setCopied] = useState(null)   // holds the copied phone value (multiple numbers now)
  const raw = c.phone ? String(c.phone).trim() : null
  const dead = c.status === 'unreachable' || c.status === 'obsolete'
  const log = (method, ph) => logContactAction(method, c.name, ph || raw, context)
  // 🔴 A BADGE TELLS YOU; IT DOES NOT LET YOU DECIDE (owner, twice, 2026-09-09: "still no
  // option to select as agent"). Badging `known_agency` answered "why is nothing shown here"
  // and left the other half of the report standing — the agency dictionary is EVIDENCE, not a
  // declaration, and there was no way to disagree with it. The 09-07 ruling stands (do not ASK
  // what we already know), so the question is not raised unprompted; the badge is simply the
  // door to it. Opt-in, one tap, and nothing changes for a viewer who accepts the answer.
  const [askOpen, setAskOpen] = useState(false)
  // A person may have several numbers (owner 2026-08-30 "one person may have many numbers"): the
  // primary rides party.primary_phone, co-listed ones live in party_contact and arrive on c.phones.
  // Fall back to the single c.phone for party-mode rows (contact book — each number is already its
  // own row) and legacy payloads that predate the array.
  const phoneList = (Array.isArray(c.phones) && c.phones.length)
    ? c.phones
    : (raw ? [{ value: raw, is_primary: true, status: c.status || null }] : [])
  const noWhatsApp = useNoWhatsApp(phoneList.map(p => p.value))
  return (
    <div className="py-1.5 border-b border-border/40 last:border-0">
      {/* THE QUESTION BELONGS TO THE CONTACT, NOT TO THE POPOVER (owner, 2026-09-07: "if one
          source has a and another b we show both, and both has its own is this agent yes no").
          It used to head the popover once, answering for the ONE listing the pill was opened
          from — which on a flat merged from two ads could only ever speak for one of them,
          while the other portal's contact sat below it unanswerable.
          ⚠️ Rendered only when the contact NAMES an ad (from_listing_id). A role report is
          listing-scoped by the 2026-07-24 ruling — the same human is owner on one ad and agent
          on another — so a cooperation or hand-added contact, which comes from no advert, has
          no listing to attach an answer to and correctly shows no control. */}
      <div className="flex items-center justify-between gap-3">
        {/* ⚠️ ★ MEANS "PREFERRED CONTACT", NOT "OWNER" — they are different claims. It is
            `default_party_id` from the contact resolver / `is_preferred` from the party book:
            the number to try FIRST. The owner is a declared ROLE and rides the badge on the
            right. Swapping this for the owner glyph would assert a role the row may not carry
            — the resolver ranks a listing author top when nobody has declared anything at all.
            Amber says PRIMARY; that is all it has ever said. Lucide `star`, not Material: this
            is a rank badge, not one of the three roles. */}
        <span className="font-medium truncate flex items-center gap-1">
          {isDefault && <Icon name="star" size={12} title={t('desk_contact_make_primary')}
            className="shrink-0 text-amber-500" />}
          {c.fb_user_id
            ? <a href={`https://www.facebook.com/${c.fb_user_id}`} target="_blank" rel="noopener noreferrer"
                 className="truncate hover:underline text-sky-500" title={t('desk_contact_fb_profile')}
                 onClick={(e) => { e.stopPropagation(); log('FB profile') }}>{c.name || t('desk_contact_unknown_fallback')}</a>
            : <span className="truncate">{c.name || t('desk_contact_unknown_fallback')}</span>}
        </span>
        {/* via_agent (2026-07-12 ownership rule): the origin was scraped by THIS agent —
            the owner is reached through them, so say so instead of the generic agent badge.
            ⚠️ This slot was `shrink-0` (fixed 2026-08-01): the via_agent text is a full
            SENTENCE, not a one-word badge, so in a 320px popover it took the whole row and
            the `truncate` on the name next to it collapsed the name to nothing — the agent
            saw a phone number with no idea whose it was. The badge shrinks and truncates
            now, capped at half the row, with the full text in the tooltip (same short-label
            + tooltip idiom the market-report reasons use). Any long label added here later
            must keep those classes. */}
        {/* 🔴 known_agency BADGES, it does not only SILENCE (owner-caught 2026-09-09:
            "the first contact is agent and I have no option to select it as agent").
            The row was Xide Partners — phone 511415911, in `known_agency_agent` — so the
            per-contact question below is correctly suppressed (we already know), and this
            branch then said NOTHING, because it only listened for via_agent/is_agent/role.
            The agent saw an unlabelled number with no way to mark it: silence read as
            "unknown", when it actually meant "known, and an agent".
            📌 The general shape, third time in this file: the condition that HIDES a question
            and the condition that SHOWS its answer must be the same set. Suppressing on a
            fact you never display is indistinguishable from having no opinion. */}
        {(c.via_agent || c.is_agent || c.role || c.known_agency) && (() => {
          // The dictionary stores the AGENCY NAME when it has one and `true` when it only
          // knows the phone is an agent's (never false — see the resolver's own note), so the
          // string is used when there is one and the generic badge when there is not.
          const agency = typeof c.known_agency === 'string' ? c.known_agency : null
          const badge = c.via_agent ? t('desk_contact_via_agent_badge')
            : c.is_agent ? t('desk_contact_agent_badge')
            // A hand-declared role outranks the dictionary: somebody looked at this person
            // and said what they are, which is a stronger statement than a phone-list hit.
            : c.role ? (t(ROLE_LABEL_KEYS[c.role]) || c.role)
            // ⚠️ AND NOT THE NAME BACK AGAIN. The dictionary's agency is very often the
            // contact's own name — the reported row IS "Xide Partners", agency "Xide
            // Partners" — and printing it as the badge gives "Xide Partners  Xide Partners",
            // which reads as a rendering fault, not as an answer. Compared loosely (case,
            // spacing) because the two strings come from different capture paths.
            : (agency && agency.replace(/\s+/g, ' ').trim().toLowerCase() !== String(c.name || '').replace(/\s+/g, ' ').trim().toLowerCase()
                ? agency : t('desk_contact_agent_badge'))
          // Both agent forms take the agent glyph — via_agent is still "reached THROUGH an
          // agent", so the shape is right and only the words differ. Stays muted: an agent on
          // this list recedes behind the owner, who is who the call is really for.
          const glyph = (c.via_agent || c.is_agent || (!c.role && c.known_agency)) ? ROLE_ICON.agent : ROLE_GLYPH[c.role]
          // Named agency: the tooltip says BOTH, so the badge can carry the name (which is
          // what the agent actually wants to read) without losing what it means.
          const title = (!c.via_agent && !c.is_agent && !c.role && agency)
            ? `${t('desk_contact_agent_badge')} · ${agency}`
            // The tenant says HOW we know (owner 2026-11-03): a closed rent deal, or a colleague's report.
            : c.role_source === 'closed_deal' ? tLog('desk_contact_tenant_since', { date: c.tenant_since ? new Date(c.tenant_since).toLocaleDateString() : '?' })
            : c.role_source === 'author_report' ? t('desk_contact_tenant_reported')
            : badge
          // Only a DICTIONARY hit is disputable here. `is_agent` is our own roster and `role`
          // is somebody's own declaration (both have their own correction paths); via_agent is
          // a statement about how the number was reached, not about who the person is.
          // A tenant label that came from colleagues' reports (role_source 'author_report') is the
          // same kind of evidence and must be just as correctable — it is the answer to that question.
          const disputable = !!(onAskRole && !c.is_agent && !c.via_agent
            && ((!c.role && c.known_agency) || c.role_source === 'author_report'))
          const inner = (
            <>
              {glyph && <Icon name={glyph} size={12} className="shrink-0" />}
              <span className="truncate">{badge}</span>
            </>
          )
          const cls = 'text-xs text-muted-foreground min-w-0 max-w-[50%] flex items-center gap-1'
          return disputable ? (
            <button type="button" className={`${cls} rounded hover:text-foreground hover:underline`}
              title={`${title} · ${t('desk_author_role_hint')}`}
              onClick={e => { e.stopPropagation(); setAskOpen(o => !o) }}>
              {inner}
            </button>
          ) : (
            <span className={cls} title={title}>{inner}</span>
          )
        })()}
      </div>
      {/* One action line PER number. The primary carries the row-level edit/remove/promote
          actions; a co-listed number is shown with its own call/copy affordances and an
          "unverified" hint, because it was observed on the ad, not confirmed as this person's. */}
      {phoneList.map((p, idx) => {
        const pv = String(p.value || '').trim()
        if (!pv) return null
        const pdigits = pv.replace(/\D/g, '')
        const pwa = pdigits.length === 9 ? `995${pdigits}` : pdigits
        const pdead = dead || p.status === 'unreachable' || p.status === 'obsolete'
        const unverified = p.status === 'unverified'
        const first = idx === 0
        return (
          <div key={pv} className="flex items-center gap-2.5 mt-0.5 text-muted-foreground">
            <button className={`tabular-nums hover:text-foreground ${pdead ? 'line-through' : ''}`} title={t('desk_contact_click_to_copy')}
              onClick={() => { navigator.clipboard?.writeText(pv); setCopied(pv); setTimeout(() => setCopied(null), 1200); log('Copied', pv) }}>
              {copied === pv ? t('desk_contact_copied') : fmtPhone(pv)}
            </button>
            {/* A number found on the ad but not confirmed for this person — shown, but marked. */}
            {unverified && !pdead && <span className="text-[10px] opacity-60" title={t('desk_contact_unverified_tip')}>?</span>}
            {!pdead && <><a href={`tel:${pv}`} onClick={() => log('Call', pv)} title={t('desk_contact_call')} className="hover:text-foreground"><Icon name="phone" /></a>
            {noWhatsApp(pv)
              // no WhatsApp on this number: the note takes the WhatsApp icon's place, call and SMS stay
              ? <span className="text-xs whitespace-nowrap" title={t('desk_contact_no_wa_tip')}>{t('desk_contact_no_wa')}</span>
              : <a href={`https://wa.me/${pwa}`} target="_blank" rel="noopener noreferrer" onClick={() => log('WhatsApp', pv)} title={t('desk_contact_whatsapp')} className="hover:text-foreground"><Icon name="message-circle" /></a>}
            <a href={`sms:${pv}`} onClick={() => log('SMS', pv)} title={t('desk_contact_sms')} className="hover:text-foreground"><Icon name="mail" /></a></>}
            {first && c.link_id && (
              <span className="ml-auto flex items-center gap-2">
                <button className="text-xs hover:text-foreground" title={t('desk_contact_edit_tooltip')} onClick={onEdit}><Icon name="pencil" /></button>
                <button className="text-xs hover:text-destructive" title={t('desk_contact_remove_tooltip')} onClick={onRemove}>✕</button>
              </span>
            )}
            {/* party contact book rows (0141): same two-icon slot, observation semantics —
                ★ promote, ✕ mark unreachable (kept as history, never deleted). */}
            {first && !c.link_id && (onMakePrimary || onUnreachable) && (
              <span className="ml-auto flex items-center gap-2">
                {onMakePrimary && !isDefault && !pdead && (
                  <button className="text-xs hover:text-foreground" title={t('desk_contact_make_primary')} onClick={onMakePrimary}>★</button>
                )}
                {onUnreachable && !pdead && (
                  <button className="text-xs hover:text-destructive" title={t('desk_contact_mark_unreachable')} onClick={onUnreachable}>✕</button>
                )}
              </span>
            )}
          </div>
        )
      })}
      {/* email-only rows (party contact book — the book holds every KIND, not just phones) */}
      {!raw && c.email && (
        <div className="flex items-center gap-2.5 mt-0.5 text-muted-foreground">
          <a href={`mailto:${c.email}`} onClick={() => log('Email')} className={`truncate hover:text-foreground ${dead ? 'line-through' : ''}`}>{c.email}</a>
          {!c.link_id && onUnreachable && !dead && (
            <button className="ml-auto text-xs hover:text-destructive" title={t('desk_contact_mark_unreachable')} onClick={onUnreachable}>✕</button>
          )}
        </div>
      )}
      {/* ONE DESIGN FOR EVERY CONTACT (owner, 2026-09-07: "make the design same for every
          contact… apply that design to all contacts of parties that aren't yet defined").
          This reuses AgentMarkLine — the control that already headed the popover — instead of a
          second, smaller question of its own. Two shapes asking the same thing in one panel is
          how a control stops reading as the same control.

          🔴 AND IT IS ONLY ASKED WHERE THE ANSWER IS UNKNOWN. It used to render for any contact
          naming an ad, so it asked "is this an agent?" about our OWN roster colleague, beside
          the აგენტი badge that already said so (owner-caught). Three things already answer it:
            • is_agent   — party.scheduler_agent_id, our roster
            • via_agent  — this row IS the agent, reached through their own listing
            • known_agency — the agency dictionary (0204) already names them; 639 non-roster
              parties are in it, and the colleague above was in BOTH.
          ⚠️ known_agency is null when unknown and a STRING/true when known, so "never heard of
          them" stays distinguishable from "checked, not an agent". */}
      {/* 🔴 AND NOT ON A CONTACT WHOSE RELATIONSHIP IS ALREADY DECLARED (owner-caught
          2026-09-09, screenshot: the question sat under a number badged კლიენტი). `c.role`
          is a hand-declared edge — client / owner / neighbour / keyholder / broker / relative
          — and every one of those already answers "who is this to the ad". Asking anyway is
          the same defect as asking it beside the აგენტი badge: a question with its answer
          printed next to it.
          ⚠️ The question is about the AD'S AUTHOR. A client's number is not the author's, so
          there is nothing here to report even if somebody pressed it. */}
      {/* `askOpen` is the badge above having been tapped — an answer we already hold, that
          this viewer wants to correct. Everything else about the gate is unchanged: we still
          never RAISE the question where a fact already answers it. */}
      {onAskRole && (askOpen || (!c.is_agent && !c.via_agent && !c.known_agency && !c.role)) && (
        <div className="mt-1" data-contact-role={c.party_id}>
          {/* 🔴 THE ANSWER MUST LAND ON SCREEN (owner-caught 2026-09-09: "second contact is
              owner and I press არა but it don't work"). It did work — the write went through —
              and `mark` was hard-coded null, so the two buttons sat there unchanged afterwards
              and the only honest reading was that the press did nothing. `roleMark` is this
              viewer's own answer, echoed back by the pill, which collapses the line to a
              statement that can be clicked to change.
              ⚠️ It is a ONE-PERSON echo on purpose. The server's reply carries a tally, but
              that tally is PROPERTY-scoped — it would print the same n beside every contact on
              the panel, which is exactly why the popover-level question was removed on 09-07. */}
          <AgentMarkLine mark={roleMark} onReport={answer => onAskRole(c, answer)} />
        </div>
      )}
    </div>
  )
}


// "contact" popover — click fetches the property's ranked contacts (same resolver the modal
// uses; falls back to the raw ad author) and lists them, numbers visible immediately.
// Opening the popover IS the deliberate reveal — logged once here with every name shown
// (per-row reveal logging died when autoReveal was introduced; this restores the audit trail).
// Pass `contacts` directly when the caller already has them loaded (the lineage modal footer)
// to skip the redundant fetch; otherwise pass `listing` and it resolves them itself
// (expanded card face, which never loads pcontacts on its own).
// Edit = dismiss the old edge + declare a new one (same POST the add form uses) — there's no
// PATCH surface to drift out of sync with resolve_party.
// Party mode (0141 contact book, owner rule 2026-07-20 "same exact design"): pass
// `partyId` (+ `partyName`) instead of listing/propertyId and the SAME pill serves a
// person's contact book — open = one logged whole-book reveal (server-side
// contact.revealed, gated by the client-ownership door), rows show every observation
// (★ preferred, struck-through unreachable), ★/✕ = promote / mark-unreachable
// (observation semantics: never deleted), add = addPartyContact (value only — the
// party IS the name). Legacy pre-0141 parties get their scalar as a pseudo-row
// (no contact_id → no actions until the first write seeds it, 0144).
// `iconOnly` (2026-07-24): drop the word, keep 📞, move the label into title=. BtnLabel
// already hides its text under `sm:`, but that keys off the VIEWPORT — and the constraint
// here is the CARD, which is ~355px in a 3-up grid on a 1400px screen. So on desktop the
// label stayed and ate the action row until "კონტაქტი" was truncated to "c".
// `dropUp` REMOVED 2026-07-25 — direction is measured, not declared; see toggle() below.
// `known` (2026-07-25, leads mobile review): tri-state ring. null = today's behaviour, a solid
// chip ("a thing that's there") — right for a property, which practically always has a contact.
// false = DASHED, the same add-affordance idiom the card footer's ＋ pills use: this surface
// knows there is no number on file yet. Leads are the case that needs it — `has_phone` is the
// lead's central question, and it used to be answered by a green/amber TEXT chip which the owner
// removed ("we don't need ნომერი აღებულია"): the state now rides on the pill the agent taps
// anyway, so one control both says whether there's a number and opens it. The pill itself does
// NOT decide this — `known` is supplied by the feed that has the fact.
export function ContactPill({ listing, contacts: preloaded, defaultPartyId: preloadedDefault, label = 'contact', address, propertyId, onAdded, chip, iconOnly = false, partyId, partyName, requirementId, known = null, agentMark = null, onAgentMarked, extraContacts = null, requireRoles = null, satisfiedRoles = null, hideOtherAgents = false }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)  // { top | bottom, left } in viewport px, for the portal
  const [fetched, setFetched] = useState(null)  // null = not fetched yet
  const [fetchedDefault, setFetchedDefault] = useState(null)
  const [hiddenStubs, setHiddenStubs] = useState([])
  const [addForm, setAddForm] = useState(null)  // { name, phone, role, editLinkId? } — null = closed
  const [saving, setSaving] = useState(false)
  const btnRef = useRef(null)
  const panelRef = useRef(null)
  const { agentId: viewerAgentId } = useViewer()
  // The answer this viewer just gave. `agentMark` is the feed's page-level read and is
  // owned by the parent, so a fresh report is held here and overlaid at render rather
  // than written into someone else's map — and it survives the popover closing and
  // reopening without forcing a refetch of the whole page.
  const [markEcho, setMarkEcho] = useState(null)
  // This viewer's own per-contact author-role answers, keyed by party_id. Local only: there is
  // no per-contact read (the server's marks are property-scoped), so this echo is the only
  // thing that can make the press visible. It lives as long as the panel does, which is long
  // enough — reopening re-asks, and answering twice is idempotent (the report table's
  // on_conflict is (listing_id, reporter_party_id)).
  const [roleEcho, setRoleEcho] = useState({})
  const mark = markEcho || agentMark
  // `extraContacts` (2026-09-06, the meeting pill): rows the CALLER already knows — a meeting's
  // client, representatives and attending agents — listed ahead of the property's own contacts,
  // which this pill still fetches as before. They are display rows, not property edges: no
  // edit/remove door is offered on them (ContactRow gates those on link_id, which they lack).
  // Nothing else about the pill changes; a caller that passes none gets the pill as it was.
  const extra = Array.isArray(extraContacts) ? extraContacts : []
  const base = preloaded ?? fetched
  // ONE PERSON, ONE ROW. A caller row that names a core party (`core_party_id`) or a number
  // hides the property's own row for the same party or the same number — a meeting's
  // representative is usually also the ad's agent, and appeared twice (owner, 2026-09-06).
  // Numbers compare on their last nine digits, so +995 5xx and 5xx are one phone.
  const tail9 = ph => { const d = String(ph || '').replace(/\D/g, ''); return d.length >= 9 ? d.slice(-9) : null }
  const extraParties = new Set(extra.map(e => e.core_party_id).filter(Boolean))
  const extraPhones = new Set(extra.map(e => tail9(e.phone)).filter(Boolean))
  // ── `hideOtherAgents` — A MEETING IS NOT THE PROPERTY'S ADDRESS BOOK (owner 2026-09-09) ──
  // "on meeting we dont need contact info of other agents who are just cooperating with owner.
  // we only need to show only involved parties in that meeting — client, owner,
  // representatives who's going."
  //
  // The property's ranked list is the right answer on a CARD, where "who do I call about this
  // flat" includes every agent working it. On a meeting it is the wrong question: the people
  // who matter are the ones in the room, and a colleague who merely co-operates on the flat is
  // a number nobody at that viewing needs.
  //
  // ⚠️ IT DROPS AGENTS, NOT EVERYONE ELSE. The keyholder is the person who opens the door and
  // the neighbour may be how you get in — cutting the list to owner-only would remove exactly
  // the contacts a viewing runs on. The representatives who ARE attending are already in
  // `extra`, put there by meetingContacts.js, so nothing involved is lost by this filter.
  const agentish = c => c.is_agent || c.via_agent || c.known_agency || c.role === 'broker'
  const dedupedBase = (base || [])
    .filter(c => !extraParties.has(c.party_id) && !(tail9(c.phone) && extraPhones.has(tail9(c.phone))))
    .filter(c => !hideOtherAgents || !agentish(c))
  const contacts = base === null && !extra.length ? null : [...extra, ...dedupedBase]
  const defaultPartyId = preloaded !== undefined ? preloadedDefault : fetchedDefault
  // `requireRoles` (owner, 2026-09-06: "every meeting must have client and owner party; if any is
  // missing show it in the popup and make it fillable"). A role the list does not carry becomes a
  // PLACEHOLDER ROW — named, marked missing — that opens the add form with that role preset. It is
  // judged only once the list is known (never against a still-loading list), and `satisfiedRoles`
  // lets the caller vouch for a party that exists without a visible row — a core meeting's client
  // is a party behind the access gate, not an absence.
  // ⚠️ JUDGED ONLY ONCE THE PROPERTY LIST IS KNOWN. With caller rows present, `contacts` is
  // never null — so the first version declared the owner missing while the property contacts
  // were still on the wire, and the owner's screenshot showed exactly that: "მესაკუთრე — არ
  // არის" beside a property whose contact list had the owner on it. `baseKnown` is the fact
  // to wait for; until then the popover shows the caller's rows and a loading line.
  const baseKnown = preloaded !== undefined || fetched !== null
  // The OWNER side is satisfied by a declared owner OR by a plain property contact: the
  // resolver's list is ranked owner-first, and the ad's author (★ in the property modal)
  // carries no declared role at all. Requiring the literal role would call the owner missing
  // on nearly every property. Agents, "reached through an agent" rows and every declared
  // non-owner role (caller, neighbour, …) do not count.
  const ownerish = c => c.role === 'owner' || (!c.is_agent && !c.via_agent && !c.role)
  const roleMet = r => contacts.some(c => c.role === r) || (r === 'owner' && contacts.some(ownerish))
  const missingRoles = (!baseKnown || contacts === null || !requireRoles?.length) ? []
    : requireRoles.filter(r => !(satisfiedRoles || []).includes(r) && !roleMet(r))
  // A property can have several live postings (myhome/ss/korter, each its own price/facts —
  // 0041) — logging just the address left no way to tell WHICH posting an agent was looking at
  // when they called/WhatsApped a contact, so "which listing got interest" was unanswerable
  // from the audit trail alone (2026-07-10, agent-directed). When this pill is rendered FROM a
  // specific listing face (the card-level 📞 chip, not the property-level footer popover which
  // has no one listing to point to), the logged context carries that listing's platform + price
  // + a short id suffix — enough to disambiguate even two same-platform postings at the same
  // price, without needing a schema change (agent_logs has no listing_id column; this matches
  // how address context already rides in the same free-text message).
  const ctx = listing
    ? `${PLATFORM_LABEL[platformKey(listing.source)] || listing.source || ''}${listing.price != null ? ` ${listing.price}${listing.currency || ''}` : ''}${listing.listing_id ? ` #${listing.listing_id.slice(0, 8)}` : ''} · ${listing.address || address || ''}`.trim()
    : address
  async function fetchContacts() {
    if (partyId) {
      // party mode: the fetch IS the logged reveal (values never ride the person payload — C1)
      try {
        // requirementId (0157): scopes the reveal door to THIS demand — the server
        // checks active representation of the named requirement, never "knows the
        // client somewhere" (invariant 6). Person modal omits it (person-scoped).
        const d = await revealContactBook(partyId, ctx || 'contact_pill', requirementId)
        setFetched((d.book || []).map((b, i) => ({
          party_id: b.contact_id || `legacy-${b.kind}-${i}`,
          contact_id: b.contact_id,
          name: partyName || d.name,
          phone: b.kind === 'phone' ? b.value : null,
          email: b.kind === 'email' ? b.value : null,
          status: b.status,
          role: b.channel || (b.status !== 'active' ? b.status : null),
        })))
        setFetchedDefault((d.book || []).find(b => b.is_preferred)?.contact_id || null)
      } catch { setFetched([]) }
      return
    }
    let list = []
    if (listing?.property_id) {
      const pc = await getPropertyContacts(listing.property_id).catch(() => null)
      // Keep a phone-LESS contact when we still have a way to identify/reach them — an FB owner
      // saved before they posted a number has a name + profile link worth showing (owner 2026-08-29).
      list = (pc?.contacts || []).filter(c => c.phone || c.fb_user_id)
      setFetchedDefault(pc?.default_party_id || null)
      // VISIBILITY §1.4: other agents' declared contacts arrive as value-less stubs —
      // the fact + recorder, never the person. Hidden must read as hidden, not absent.
      setHiddenStubs(pc?.hidden_declared || [])
    }
    if (!list.length && listing?.author_phone) list = [{ party_id: 'author', name: listing.author_name, phone: listing.author_phone, role: 'listing author' }]
    setFetched(list)
  }
  // After add/edit/remove: parent-owned lists reload via onAdded (footer → reloadContacts),
  // self-fetched ones refetch here — both, since only the caller knows which it is.
  function refresh() { if (preloaded === undefined) fetchContacts(); onAdded?.() }
  // 0376. Scoped to THIS AD — the same human is an owner on one listing and an agent on
  // another, so the report names the listing and the person-level pattern is projected at
  // read time by the server (`elsewhere`), never asserted here.
  // Per contact (2026-09-07). Same endpoint as the header question, but naming the listing
  // THIS contact came from instead of the one the pill happens to be showing — which is the
  // whole point on a flat merged from two ads.
  async function askContactRole(c, answer) {
    // The ad THIS contact came from, falling back to the one the pill is showing — which is
    // exactly what the popover-level question always reported against. Without the fallback a
    // cooperation or hand-added contact would draw the control and the press would do nothing:
    // a door with nothing behind it, which is worse than no door.
    const target = c?.from_listing_id || listing?.listing_id
    if (!target) return
    // ⚠️ THE PRESS USED TO BE SILENT IN BOTH DIRECTIONS, and that is what "it don't work"
    // meant. On success nothing changed (see the AgentMarkLine note below); on failure the
    // rejection went nowhere — and this endpoint has three real refusals an agent can hit
    // (no party for this account, several parties on one email, listing not found), each of
    // which is a sentence the person pressing the button needs to read. `reportAgentRole`
    // three lines down has always alerted; this path was the copy that forgot to.
    try {
      await reportAuthorRole({ listingId: target, answer, agentId: viewerAgentId })
      setRoleEcho(m => ({ ...m, [c.party_id]: {
        reported_agent: answer === 'yes' ? 1 : 0,
        reported_owner: answer === 'no' ? 1 : 0,
        reported_tenant: answer === 'tenant' ? 1 : 0,
      } }))
      onAgentMarked?.(null)
    } catch (e) { alert(e.message) }
  }
  async function reportAgentRole(answer) {
    if (!listing?.listing_id) return
    try {
      const r = await reportAuthorRole({ listingId: listing.listing_id, answer, agentId: viewerAgentId })
      setMarkEcho(r)
      onAgentMarked?.(r)
    } catch (e) { alert(e.message) }
  }
  // Portaled to document.body with position:fixed, computed from the trigger's own screen
  // rect — NOT an absolute-positioned child of the button. A collapsed-stack card wraps its
  // front card in a z-indexed + position:relative box (to layer the "sheets behind" effect),
  // which creates its own stacking context; any z-index on a descendant is trapped inside it
  // and can never paint above a LATER sibling card in the grid, however high the z-index
  // number is (2026-07-05: the popover was rendering, correctly z-50'd, but still appearing
  // UNDER the next card down). Portaling escapes that ancestor entirely, same fix already
  // used for AddContactModal.
  function toggle(e) {
    e.stopPropagation()
    setOpen(o => {
      const next = !o
      if (next) {
        // ⚠️ Placement is popoverPos now, NOT a caller-supplied direction (2026-07-25).
        // Two bugs the old line had: (1) `dropUp` made each CALLER guess the direction, so
        // the card-footer pill — the one anchored on a card's bottom rail, i.e. the one
        // most likely to be cropped — never passed it and opened down off the phone screen;
        // (2) `left: r.left` had NO horizontal clamp, so a pill near the right edge pushed
        // the 320px panel off-screen sideways. popoverPos prefers up, clamps both axes, and
        // returns a maxHeight the panel scrolls inside. One rule for every card popover.
        setPos(popoverPos(btnRef.current, 320))
        const list = preloaded ?? fetched
        if (list?.length) logContactAction('Viewed contacts', list.map(c => c.name || c.phone).join(', '), null, ctx)
      }
      return next
    })
    if (preloaded === undefined && fetched === null) fetchContacts()
  }
  // Closing on scroll/resize avoids the panel drifting away from its trigger if the page
  // moves under it — cheaper than re-measuring on every scroll tick for a rarely-open popover.
  // …but a scroll inside the panel itself is the agent reading a long contact list, not the
  // page moving — useCloseOnPageMove draws that line, reusing this panel's own ref.
  useCloseOnPageMove(open, () => setOpen(false), panelRef)
  // No backdrop (unlike a modal) — a click anywhere outside the trigger/panel must close it,
  // same as the Agents tab's Contact submenu. Capture phase so it fires even if the click
  // target's own handler stops propagation (e.g. another card's onClick).
  useEffect(() => {
    if (!open) return
    const onDocClick = e => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('click', onDocClick, true)
    return () => document.removeEventListener('click', onDocClick, true)
  }, [open])
  const pid = propertyId || listing?.property_id
  return (
    <span className="inline-block">
      {/* chip = solid full border like the cooperation chip (NOT the dashed add-affordance
          style): a property practically always has a contact, so this reads as "a thing
          that's there", not "something missing you could add". Default = plain text link
          (modal footer). */}
      {/* chip mode: SVG phone icon; the 📞 lives inside the i18n label string (all
          languages), so it's stripped at render rather than edited out of every locale. */}
      {/* `agentMark` (0376): the ONE face signal that this ad has been reported as an
          agent's. It rides the pill the agent already taps rather than adding a fourth chip
          to the footer rail — the line budget there is the reason the rail has three groups
          and not four. `info` (sky), never `warn`: a colleague's report is a note, not an
          alarm, and the card already carries amber for things that need acting on. `add`
          still wins when there is no number at all — a phone that doesn't exist can't be
          marked, so the two states cannot collide. */}
      <button ref={btnRef}
        /* The face answers "says who?" without opening anything — names, answers and dates,
           each line naming its source. Falls back to the plain contact label when there is
           nothing recorded, so an ordinary pill keeps its ordinary tooltip. */
        title={authorMarkTooltip(mark) || (iconOnly ? label : t('desk_contact_view_add_tooltip'))}
        className={chip
          /* Only a COLLEAGUE'S report colours the face. A portal declaration alone does
             not: 15,770 listings declare "owner" and 1,097 declare "agent", so tinting on
             `declared` would paint a quarter of the feed with something no human checked.
             The declaration still shows inside the popover, where it is context rather
             than a signal. */
          ? pill(known === false ? 'add' : mark?.reported_agent > 0 ? 'info' : 'neutral', { door: true, shrink: !iconOnly, extra: 'bg-background' })
          : 'text-xs text-primary hover:underline'}
        onClick={toggle}>
        {iconOnly ? <Icon name="phone" size={14} /> : <BtnLabel icon="phone" label={label} />}
      </button>
      {open && pos && createPortal(
        <div ref={panelRef} className="fixed z-[200] bg-card border border-border rounded-lg shadow-xl py-1.5 px-3 w-[320px] text-sm overflow-y-auto overscroll-contain" style={pos}
          onClick={e => e.stopPropagation()}>
          {/* Heads the popover because it qualifies every number below it, and because the
              agent reads it on the way to the call. Property mode only: a party's own
              contact book is a person we already know, not an ad's author, so the question
              has no subject there. Needs a listing to attach to and an identity to
              attribute to — without either the server would refuse the write anyway. */}
          {/* ⚠️ NOT GATED ON viewerAgentId — that was the shipped bug (2026-08-02), and it
              is the SAME one B277 fixed for the district vote two days earlier: an admin or
              manager has no `agents` row, so myAgentId is null, so the question was hidden
              from exactly the people who asked for the feature (the owner, on his own
              login, saw nothing). The server takes `agent_id OR a verified session` and
              resolves the party through party.auth_user_id for accounts without a roster
              row — so the client must not add a precondition the API does not have. */}
          {/* 🔴 THE POPOVER-LEVEL QUESTION IS GONE (owner, 2026-09-07: "no need for that
              redundant აგენტმა დადო?"). Once every not-yet-defined contact carries its own, this
              one asked the same thing again at the top — and worse, it asked it of the LISTING
              rather than of a person, so on a panel listing three contacts it was unclear which
              of them the answer was about.
              ⚠️ What is lost with it: the property-level TALLY (how many colleagues have
              answered) and the portal claim line. Both were context for a question that now
              lives per contact, where the tally would be misleading anyway — it aggregates the
              whole flat, so the same number would sit beside every person.
              The mark/markEcho state stays: reportAgentRole is still the fallback path for a
              contact that names no ad of its own. */}
          {contacts === null ? <p className="text-xs text-muted-foreground py-1">{t('desk_loading')}</p>
            : contacts.length === 0 ? <p className="text-xs text-muted-foreground py-1">{t('desk_contact_none')}</p>
            : contacts.map(c => (
              <ContactRow key={c.party_id} c={c} isDefault={partyId ? c.contact_id === defaultPartyId : c.party_id === defaultPartyId} context={ctx}
                onAskRole={askContactRole} roleMark={roleEcho[c.party_id] || null}
                // review 2026-09-23: seed the existing note (was '' → an edit blanked it) and remember
                // who is being edited, so an unchanged name+phone keeps the SAME person.
                onEdit={() => setAddForm({ name: c.name || '', phone: c.phone || '', role: c.role || 'caller', note: c.note || '',
                  editLinkId: c.link_id, editPartyId: c.party_id, origName: c.name || '', origPhone: c.phone || '',
                  origRole: c.role || 'caller', origNote: c.note || '' })}
                onRemove={async () => {
                  if (!confirm(tLog('desk_contact_remove_confirm', { 'name/phone': c.name || c.phone }))) return
                  try { await removePropertyContact(c.link_id); refresh() } catch (e) { alert(e.message) }
                }}
                onMakePrimary={partyId && c.contact_id ? async () => {
                  try { await updatePartyContact(c.contact_id, { preferred: true }); refresh() } catch (e) { alert(e.message) }
                } : null}
                onUnreachable={partyId && c.contact_id ? async () => {
                  try { await updatePartyContact(c.contact_id, { status: 'unreachable', note: 'marked from contact pill' }); refresh() } catch (e) { alert(e.message) }
                } : null}
 />
            ))}
          {/* The property's own contacts still loading behind the caller's rows. */}
          {!baseKnown && contacts !== null && listing?.property_id && (
            <p className="text-xs text-muted-foreground py-1">{t('desk_loading')}</p>
          )}
          {/* A required party with nobody on record. Rendered where the row would be, in the
              role's own glyph, so the gap is read in the same pass as the people — and it IS
              the door: one tap opens the add form with the role already chosen. Without a
              property to attach to (a legacy meeting with no core property) it can only say so. */}
          {missingRoles.map(r => (
            <button key={`missing-${r}`} type="button" disabled={!pid}
              className="w-full text-left py-1.5 border-b border-border/40 last:border-0 flex items-center gap-1.5 text-xs disabled:cursor-default"
              title={pid ? t('desk_contact_add_button') : t('desk_contact_role_missing_no_door')}
              onClick={() => setAddForm({ name: '', phone: '', role: r, note: '' })}>
              {ROLE_GLYPH[r] && <Icon name={ROLE_GLYPH[r]} size={12} className="shrink-0 text-amber-500" />}
              <span className="font-medium">{t(ROLE_LABEL_KEYS[r]) || r}</span>
              <span className="text-amber-600 dark:text-amber-400">— {t('desk_contact_role_missing')}</span>
              {pid && <span className="ml-auto text-primary">{t('desk_contact_add_button')}</span>}
            </button>
          ))}
          {/* Others' declared contacts: the FACT + recorder, values withheld (§1.4). */}
          {hiddenStubs.map((h, i) => (
            <div key={`${h.declared_at || 'stub'}-${i}`} className="flex items-center gap-1.5 py-1 text-xs text-muted-foreground">
              <Icon name="lock" size={11} />
              <span>{h.declared_by_name
                ? tLog('desk_contact_hidden_by', { name: h.declared_by_name })
                : t('desk_contact_hidden_some')}</span>
            </div>
          ))}
          {(pid || partyId) && (addForm ? (
            <div className="pt-2 mt-1 border-t border-border/50 space-y-1.5">
              <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                placeholder={partyId ? t('desk_contact_add_ph') : t('desk_contact_add_phone_ph')} value={addForm.phone}
                onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))} />
              {/* party mode: value only — the party IS the name, and roles/notes are a
                  property-edge concept. Same phone-shape nudge as everywhere else. */}
              {partyId && !addForm.phone.includes('@') && addForm.phone.trim() && (() => {
                const w = checkPhone(...splitDial(addForm.phone, '+995'))
                return w ? <p className="text-xs text-amber-600 dark:text-amber-400">
                  {w.reason === 'lead' ? t('desk_phone_shape_lead')
                    : tLog('desk_phone_shape_len', { cc: w.cc ? w.cc + ' ' : '', want: w.want, got: w.got })}</p> : null
              })()}
              {!partyId && <>
              <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" placeholder={t('desk_contact_add_name_ph')} value={addForm.name}
                onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} />
              {/* Stored role values stay English (evidence jsonb reads/filters expect them);
                  only the caller label is Georgian — "interested person", not yet a client. */}
              <select className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" value={addForm.role}
                onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}>
                {[['caller', 'დაინტერესებული პირი'], ['client', t('desk_role_client')], ['owner', t('desk_role_owner')], ['neighbor', t('desk_role_neighbor')], ['keyholder', t('desk_role_keyholder')], ['broker', t('desk_role_broker')], ['relative', t('desk_role_relative')], ['tenant', t('desk_role_tenant')], ['other', t('desk_role_other')]]
                  .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <textarea className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" rows={2}
                placeholder={t('desk_contact_notes_ph')} value={addForm.note}
                onChange={e => setAddForm(f => ({ ...f, note: e.target.value }))} />
              </>}
              <div className="flex justify-end gap-1.5 pt-0.5">
                <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setAddForm(null)}>{t('desk_client_cancel')}</button>
                <button className="text-xs text-primary font-medium disabled:opacity-50"
                  disabled={saving || (partyId ? !addForm.phone.trim() : (!addForm.phone.trim() && !addForm.name.trim()))}
                  onClick={async () => {
                    setSaving(true)
                    try {
                      if (partyId) {
                        const v = addForm.phone.trim()
                        await addPartyContact(partyId, { kind: v.includes('@') ? 'email' : 'phone', value: v })
                      } else {
                        // review 2026-09-23: ADD FIRST, then dismiss the old edge — only once the new one
                        // exists. Remove-then-add lost the contact whenever the add failed, and a retry
                        // then re-removed a dismissed edge and errored.
                        const editId = addForm.editLinkId || null
                        const digits = s => String(s || '').replace(/\D/g, '').replace(/^00/, '').replace(/^995/, '').replace(/^0+/, '')
                        const samePerson = !!editId && !!addForm.editPartyId
                          && addForm.name.trim() === (addForm.origName || '').trim() && digits(addForm.phone) === digits(addForm.origPhone)
                        const body = { name: addForm.name, phone: addForm.phone, role: addForm.role, note: addForm.note?.trim() || null,
                          ...(samePerson ? { partyId: addForm.editPartyId } : {}), editLinkId: editId || undefined }
                        const r = await addPropertyContact(pid, body)
                        if (editId && r?.link_id && r.link_id !== editId) {
                          await removePropertyContact(editId)
                        } else if (editId && r?.link_id === editId
                          && (addForm.role !== addForm.origRole || (addForm.note || '').trim() !== (addForm.origNote || '').trim())) {
                          // The server absorbed the edit into the edge being edited (it does not know
                          // which edge is being replaced until addPropertyContact forwards edit_link_id),
                          // so a role/note change would silently not apply. Replace it the old way —
                          // the add just succeeded, so the re-add is the same request.
                          await removePropertyContact(editId)
                          await addPropertyContact(pid, body)
                        }
                      }
                      setAddForm(null); refresh()
                    } catch (e) { alert(e.message) } finally { setSaving(false) }
                  }}>{saving ? t('desk_property_saving') : t('save')}</button>
              </div>
            </div>
          ) : (
            <button className="text-xs text-primary pt-1.5 mt-1 border-t border-border/50 w-full text-left"
              onClick={() => setAddForm({ name: '', phone: '', role: 'caller', note: '' })}>{t('desk_contact_add_button')}</button>
          ))}
        </div>,
        document.body
      )}
    </span>
  )
}
